import { useEffect, useState } from 'react';
import { useAnalyticsStore } from '@/store/analytics.store';
import '@/styles/analytics.css';

const RADIUS = 70;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function getScoreColor(score: number): string {
  if (score >= 70) return 'var(--color-success)';
  if (score >= 50) return 'var(--color-warning)';
  return 'var(--color-error)';
}

export function ScoreGauge() {
  const { avgMatchScore, totalJobs } = useAnalyticsStore((s) => s.overview);

  // Animate: start with full offset (no progress), then transition to target
  const [animatedOffset, setAnimatedOffset] = useState(CIRCUMFERENCE);

  useEffect(() => {
    // Delay slightly to trigger CSS transition after mount
    const targetOffset =
      totalJobs > 0
        ? CIRCUMFERENCE - (avgMatchScore / 100) * CIRCUMFERENCE
        : CIRCUMFERENCE;

    const timer = setTimeout(() => {
      setAnimatedOffset(targetOffset);
    }, 100);

    return () => clearTimeout(timer);
  }, [avgMatchScore, totalJobs]);

  const color = getScoreColor(avgMatchScore);

  if (totalJobs === 0) {
    return (
      <div className="score-gauge">
        <h2 className="score-gauge__title">ATS Match Score</h2>
        <div className="score-gauge__empty">
          <svg className="score-gauge__empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          <p className="score-gauge__empty-text">
            No jobs analyzed yet. Add jobs to your pipeline to see your average ATS score.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="score-gauge">
      <h2 className="score-gauge__title">ATS Match Score</h2>
      <div className="score-gauge__chart">
        <svg width="180" height="180" viewBox="0 0 180 180" aria-label={`ATS score: ${avgMatchScore}%`}>
          {/* Background track */}
          <circle
            cx="90"
            cy="90"
            r={RADIUS}
            fill="none"
            stroke="var(--color-border)"
            strokeWidth="12"
          />
          {/* Filled arc */}
          <circle
            cx="90"
            cy="90"
            r={RADIUS}
            fill="none"
            stroke={color}
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={animatedOffset}
            style={{
              transform: 'rotate(-90deg)',
              transformOrigin: 'center',
              transition: 'stroke-dashoffset 2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />
        </svg>
        <div className="score-gauge__center-text">
          <span className="score-gauge__score">{avgMatchScore}</span>
          <span className="score-gauge__score-label">ATS Score</span>
        </div>
      </div>
      <p className="score-gauge__footer">
        Based on {totalJobs} analyzed {totalJobs === 1 ? 'job' : 'jobs'}
      </p>
    </div>
  );
}
